import React, { useState } from 'react';
import {
    Box,
    TextField,
    IconButton,
    InputAdornment,
    Alert,
    Snackbar,
    Grid,
} from '@mui/material';
import {
    Save as SaveIcon,
    Key as KeyIcon,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { PageTitle } from '../../../shared/components/PageTitle';
import { SettingsCard } from '../../../shared/components/SettingsCard';

export const ChangePassword: React.FC = () => {
    const { changePassword } = useAuth();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setError("New password and confirm password don't match");
            return;
        }

        if (newPassword.length < 8) {
            setError("New password must be at least 8 characters long");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await changePassword({ oldPassword, newPassword });
            setSuccess('Password changed successfully');
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setError(null);
        setSuccess(null);
    };

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            height: '54px',
            border: '1px solid #e2e8f0',
            '& fieldset': {
                borderColor: 'transparent',
            },
            '&:hover fieldset': {
                borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'transparent',
                border: '1px solid #6366f1'
            },
        },
        '& .MuiInputBase-input': {
            color: '#1f2937',
            padding: '12px 14px',
            fontSize: '15px'
        }
    };

    return (
        <div className="add-edit-admin-container bg-[#F0F4F8] min-h-[calc(100vh-64px)] py-10 px-4">
            <div className="max-w-5xl mx-auto">
                <PageTitle title="Security Settings" />

                <SettingsCard
                    title="Update Password"
                    subtitle="Ensure your account is using a long, random password to stay secure."
                    icon={<KeyIcon sx={{ fontSize: 36, color: '#6366f1' }} />}
                >
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">Current Password</label>
                                <TextField
                                    type={showOldPassword ? 'text' : 'password'}
                                    fullWidth
                                    required
                                    value={oldPassword}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                    placeholder="Enter current password"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end" sx={{ color: '#94A3B8' }}>
                                                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldStyles}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">New Password</label>
                                <TextField
                                    type={showNewPassword ? 'text' : 'password'}
                                    fullWidth
                                    required
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" sx={{ color: '#94A3B8' }}>
                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldStyles}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">Confirm New Password</label>
                                <TextField
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    fullWidth
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Repeat new password"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: '#94A3B8' }}>
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldStyles}
                                />
                            </Grid>
                        </Grid>

                        <div className="mt-12 flex justify-end">
                            <AppButton
                                type="submit"
                                variant="primary"
                                startIcon={<SaveIcon />}
                                loading={loading}
                                sx={{
                                    py: 1,
                                    px: 4,
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    backgroundColor: '#6366f1',
                                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#4f46e5',
                                    }
                                }}
                            >
                                Update Password
                            </AppButton>
                        </div>
                    </form>
                </SettingsCard>
            </div>

            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" variant="filled" sx={{ width: '100%', borderRadius: '12px' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" variant="filled" sx={{ width: '100%', borderRadius: '12px' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
